/**
 * Host-to-attendees broadcast composer (README §3 — MESSAGE ALL / BROADCAST). Posts a
 * single message that fans out to a host-thread for every accepted/paid attendee, or
 * opens one group room. The ALLOW REPLIES switch decides whether the resulting threads
 * accept attendee responses or are read-only announcements. Moved out of
 * EventOperationPage unchanged in behaviour; restyled on the kit.
 */
import React, { useEffect, useState } from 'react';
import { SurfaceButton, SwitchRow, MUTED, SUBTLE, DANGER, GREEN } from '../surface';
import { HostTextarea } from './hostBits';

export default function BroadcastModal({ isOpen, onClose, eventId, eventTitle, attendeeCount }) {
  const [body, setBody] = useState('');
  const [allowReplies, setAllowReplies] = useState(true);
  // 'fan_out' → 1 thread per attendee (classic broadcast); 'group' → one shared room.
  const [mode, setMode] = useState('fan_out');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setBody(''); setAllowReplies(true); setMode('fan_out'); setSending(false); setResult(null); setError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const submit = async () => {
    if (!body.trim() || sending) return;
    setSending(true);
    setError(null);
    const messageService = (await import('../../services/messageService')).default;
    const r = await messageService.broadcastToEvent(eventId, body.trim(), allowReplies, mode);
    setSending(false);
    if (r.success) setResult({ ...r.data, mode });
    else setError(r.error || 'Broadcast failed');
  };

  const modeBtn = (key, title, desc) => (
    <button
      type="button"
      onClick={() => setMode(key)}
      className={`border-2 border-black p-3 text-left transition-colors duration-200 ${mode === key ? 'bg-black text-white' : 'bg-white text-black hover:bg-black hover:text-white'}`}
    >
      <div className="font-mono font-bold uppercase" style={{ fontSize: 11, letterSpacing: '0.1em' }}>{title}</div>
      <div className="font-mono" style={{ fontSize: 10, marginTop: 4, opacity: 0.8 }}>{desc}</div>
    </button>
  );

  return (
    <div onClick={onClose} className="fixed inset-0 safe-inset z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }} data-testid="broadcast-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full border-2 border-black bg-white" style={{ maxWidth: 560, boxShadow: '4px 4px 0 0 #000' }}>
        <div className="border-b-2 border-black" style={{ padding: '14px 18px' }}>
          <div className="font-mono font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.2em', color: MUTED }}>HOST → ATTENDEES</div>
          <div className="font-mono font-bold uppercase" style={{ fontSize: 14, letterSpacing: '0.05em', marginTop: 4 }}>BROADCAST</div>
          <div className="truncate font-mono" style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{eventTitle || ''}</div>
        </div>
        {result ? (
          <div className="flex flex-col items-center gap-3 text-center" style={{ padding: 24 }}>
            <div className="font-mono font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.2em', color: GREEN }}>SENT</div>
            <p className="font-mono" style={{ fontSize: 13, margin: 0 }}>
              {result.mode === 'group'
                ? <>Group chat opened with <strong>{result.recipientCount}</strong> member{result.recipientCount === 1 ? '' : 's'} + you.</>
                : <>Reached <strong>{result.recipientCount}</strong> attendee{result.recipientCount === 1 ? '' : 's'}.</>}
            </p>
            <p className="font-mono" style={{ fontSize: 11, color: MUTED, margin: 0 }}>
              {result.allowReplies ? 'They can reply.' : 'Replies are off — read-only on their side.'}
            </p>
            <SurfaceButton variant="fill" size="sm" onClick={onClose}>DONE</SurfaceButton>
          </div>
        ) : (
          <div className="flex flex-col gap-4" style={{ padding: 18 }}>
            <div className="font-mono" style={{ fontSize: 11, color: MUTED }}>
              Will send to <strong>{attendeeCount ?? 0}</strong> accepted/paid attendee{attendeeCount === 1 ? '' : 's'}.
            </div>
            <div>
              <div className="font-mono font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.1em', color: MUTED, marginBottom: 6 }}>MODE</div>
              <div className="grid grid-cols-2 gap-2">
                {modeBtn('fan_out', '1:1 THREADS', 'Each guest gets their own thread with you.')}
                {modeBtn('group', 'GROUP CHAT', 'One shared room. Everyone sees + can reply.')}
              </div>
            </div>
            <div>
              <HostTextarea
                value={body}
                onChange={(e) => setBody(e.target.value.slice(0, 2000))}
                placeholder="What do you want to tell everyone?"
                rows={5}
                aria-label="Broadcast message"
              />
              <div className="text-right font-mono" style={{ fontSize: 10, letterSpacing: '0.1em', color: SUBTLE, marginTop: 4 }}>{body.length} / 2000</div>
            </div>
            <SwitchRow
              boxed
              dense
              label={`ALLOW ${mode === 'group' ? 'MEMBERS' : 'ATTENDEES'} TO REPLY`}
              desc={mode === 'group'
                ? (allowReplies ? 'Everyone in the room can post.' : 'Announcements only — only you can post.')
                : (allowReplies ? 'Each attendee gets their own thread with you. They can write back.' : "Read-only announcement. Attendees see it but can't respond.")}
              checked={allowReplies}
              onChange={setAllowReplies}
            />
            {error && (
              <div className="font-mono font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.1em', color: DANGER, border: `2px solid ${DANGER}`, padding: '8px 10px' }}>{error}</div>
            )}
            <div className="flex justify-end gap-2">
              <SurfaceButton variant="outline" size="sm" onClick={onClose} disabled={sending}>CANCEL</SurfaceButton>
              <SurfaceButton variant="fill" size="sm" onClick={submit} disabled={!body.trim() || sending || (attendeeCount ?? 0) === 0}>
                {sending ? 'SENDING…' : 'SEND BROADCAST'}
              </SurfaceButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
